'use client';

import { Box, css } from '@terra/ui-styled-system';
import { Cancel01Icon } from '@terra/ui-icons';
import { Portal } from '@ark-ui/react';
import {
  forwardRef,
  useState,
  type ComponentProps,
  type ComponentType,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { HoverCard } from '../../../components/hover-card';
import { IconButton } from '../../../components/icon-button';
import { MetaDefault } from '../../../components/typography';

const pillStyles = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 1,
  px: 1.5,
  py: 0.5,
  maxW: '220px',
  minH: '20px',
  borderRadius: 'sm',
  borderWidth: '1px',
  borderColor: 'border.subtle',
  bg: 'bg.subtle',
  verticalAlign: 'middle',
  mx: 0.5,
  '&[data-tone="mention"]': {
    bg: 'terra.a1',
    borderColor: 'terra.a3',
    color: 'terra.11',
  },
  '&[data-tone="mention"]:hover': {
    bg: 'terra.a2',
    borderColor: 'terra.a4',
  },
  '&[data-status="error"]': {
    borderColor: 'border.error',
  },
});

export type ContextPillIcon = ComponentType<{ size?: number }>;

export type ContextPillProps = {
  label: string;
  icon?: ContextPillIcon;
  thumbnailUrl?: string;
  onRemove?: (event: MouseEvent<HTMLButtonElement>) => void;
  removeAriaLabel?: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  preview?: ReactNode;
  status?: 'default' | 'uploading' | 'error';
  tone?: 'neutral' | 'mention';
  detail?: string;
} & Omit<ComponentProps<'span'>, 'onClick' | 'children' | 'color'>;

export const ContextPill = forwardRef<HTMLSpanElement, ContextPillProps>(
  function ContextPill(
    {
      label,
      icon: Icon,
      thumbnailUrl,
      onRemove,
      removeAriaLabel,
      onClick,
      preview,
      status = 'default',
      tone = 'neutral',
      detail,
      ...spanProps
    },
    ref,
  ) {
    const [isHovered, setIsHovered] = useState(false);
    const showRemove = Boolean(isHovered && onRemove);
    const isError = status === 'error';
    const isMention = tone === 'mention';
    const contentColor = isError
      ? 'fg.error'
      : isMention
        ? 'terra.11'
        : 'fg.default';
    const iconColor = isError
      ? 'fg.error'
      : isMention
        ? 'terra.11'
        : 'fg.muted';

    const pill = (
      <span
        ref={ref}
        className={pillStyles}
        data-status={status}
        data-tone={tone}
        style={{ cursor: onClick || onRemove ? 'pointer' : 'default' }}
        title={detail ? `${label} · ${detail}` : label}
        {...spanProps}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={onClick}
      >
        <Box
          as="span"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          color={iconColor}
          flexShrink={0}
          w="14px"
          h="14px"
        >
          {showRemove ? (
            <IconButton
              type="button"
              variant="ghost"
              size="xs"
              aria-label={removeAriaLabel}
              w="14px"
              h="14px"
              minW="14px"
              p="0"
              color="inherit"
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onRemove?.(event);
              }}
            >
              <Cancel01Icon size={12} />
            </IconButton>
          ) : thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt=""
              style={{
                width: 14,
                height: 14,
                objectFit: 'cover',
                borderRadius: 2,
                display: 'block',
              }}
            />
          ) : Icon ? (
            <Icon size={12} />
          ) : null}
        </Box>
        <MetaDefault color={contentColor} truncate minW={0}>
          {detail ? `${label} · ${detail}` : label}
        </MetaDefault>
      </span>
    );

    if (!preview) {
      return pill;
    }

    return (
      <HoverCard.Root
        lazyMount
        unmountOnExit
        openDelay={300}
        closeDelay={100}
        positioning={{ placement: 'top' }}
      >
        <HoverCard.Trigger asChild>{pill}</HoverCard.Trigger>
        <Portal>
          <HoverCard.Positioner>
            <HoverCard.Content p={2} maxW="360px">
              {preview}
            </HoverCard.Content>
          </HoverCard.Positioner>
        </Portal>
      </HoverCard.Root>
    );
  },
);
